import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loadable, useLoad, fmt, fmtDay, Status, ActionDialog } from "@/components/control/kit";
import { V3Page, AmberAside, Field, FeatureRadios, V3Actions, useRun, ErrorLine, FilterEmpty } from "@/components/control/v3";

const ASIDE = <AmberAside title="Unbekannt bleibt offen" text="Eine ungeklärte Angabe ist keine Bestätigung der Zugänglichkeit." />;

export default function AccessGaps() {
  const st = useLoad("/manage/activity-access-gaps");
  return (
    <V3Page crumb="Aktivitäten" title="Erforderliche Zugangsangaben fehlen" subtitle="Die fehlende Angabe bleibt offen, bis eine nachvollziehbare Quelle vorliegt."
      aside={ASIDE} testId="access-gaps-page">
      <Loadable state={st}>
        {(d) => (d.activities.length === 0 ? <FilterEmpty title="Keine offenen Zugangsangaben" filters={[["Status", "Offen"], ["Bereich", "Zugangsangaben"]]} /> : (
          <ul className="space-y-2.5" data-testid="access-gaps-list">
            {d.activities.map((a) => (
              <li key={a.id}>
                <Link to={`/aktivitaeten/zugangsluecken/${a.id}`} data-testid={`access-gap-${a.id}`}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E6E1F0] bg-white px-4 py-3 transition-colors hover:border-brand-magenta">
                  <span><span className="block font-semibold text-brand-purple">{a.title}</span>
                    <span className="text-sm text-zinc-600">{fmt(a.date)} · offen: {a.missing.join(", ")}</span></span>
                  <Status value={a.status} />
                </Link>
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </V3Page>
  );
}

function GapView({ d, reload }) {
  const a = d.activity;
  const navigate = useNavigate();
  const current = a.access?.features || {};
  const [values, setValues] = useState(() => Object.fromEntries(Object.keys(d.features).map((k) => [k, current[k]?.value || "unbekannt"])));
  const [source, setSource] = useState("");
  const [ask, setAsk] = useState(false);
  const { busy, error, run } = useRun();
  const changed = Object.keys(values).some((k) => values[k] !== (current[k]?.value || "unbekannt"));
  const sources = Object.values(current).filter((f) => f?.source);
  const latest = sources.sort((x, y) => (x.updated_at < y.updated_at ? 1 : -1))[0];
  const save = async () => {
    const body = { features: values, directions: a.access?.directions || "", source };
    if (await run(() => api.put(`/manage/activities/${a.id}/access`, body), "Zugangsangaben gespeichert.")) reload();
  };
  return (
    <V3Page crumb="Aktivitäten" title="Erforderliche Zugangsangabe fehlt" subtitle="Die fehlende Angabe bleibt offen, bis eine nachvollziehbare Quelle vorliegt."
      aside={ASIDE} testId="access-gap-detail">
      <p className="font-semibold text-brand-purple" data-testid="access-gap-title">{a.title_de}</p>
      {Object.entries(d.features).map(([k, l]) => (
        <FeatureRadios key={k} id={k} label={l} value={values[k]} disabled={!d.can.manage} onChange={(v) => setValues((s) => ({ ...s, [k]: v }))} />
      ))}
      <Field label="Quelle" testId="access-gap-source">{latest?.source || "Noch keine Quelle erfasst"}</Field>
      <Field label="Stand" testId="access-gap-date">{latest ? fmtDay(latest.updated_at) : "–"}</Field>
      {changed && (
        <div className="space-y-2.5 rounded-xl border border-[#E6E1F0] bg-white p-4" data-testid="access-gap-save">
          <label htmlFor="access-gap-source-input" className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-brand-purple">Quelle der geänderten Angaben *</span>
            <input id="access-gap-source-input" value={source} onChange={(e) => setSource(e.target.value)} data-testid="access-gap-source-input"
              className="h-11 rounded-xl border border-[#E6E1F0] px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta" />
          </label>
          <V3Actions primary="Angaben speichern" onPrimary={save} busy={busy} secondary={null} testId="access-gap-store" />
        </div>
      )}
      <ErrorLine error={error} testId="access-gap-error" />
      <V3Actions primary={d.can.manage ? "Rückfrage prüfen" : null} onPrimary={() => setAsk(true)} secondary="Zurück"
        onSecondary={() => navigate("/aktivitaeten/zugangsluecken")} testId="access-gap" />
      <ActionDialog open={ask} onOpenChange={setAsk} title="Rückfrage zu Zugangsangaben" testId="access-request-dialog" submitLabel="Rückfrage senden"
        description="Die Rückfrage geht an die bestätigte Leitung und die zuständige Standortkoordination der Organisation." onDone={reload}
        fields={[{ name: "message", label: "Rückfrage", type: "textarea", required: true,
          default: `Bitte ergänzen: ${Object.entries(d.features).filter(([k]) => values[k] === "unbekannt").map(([, l]) => l).join(", ")}. Bitte Quelle und Stand angeben.` }]}
        onSubmit={async (v) => {
          const { data } = await api.post(`/manage/activities/${a.id}/access-request`, v);
          return data.recipients ? `Rückfrage an ${data.recipients} Person(en) gesendet.` : "Rückfrage im Verlauf gespeichert. Noch keine bestätigte Organisationsrolle als Empfänger.";
        }} />
    </V3Page>
  );
}

export function AccessGapDetail() {
  const { id } = useParams();
  const st = useLoad(`/manage/activities/${id}`);
  return <Loadable state={st}>{(d) => <GapView key={d.activity.access?.open_count} d={d} reload={st.reload} />}</Loadable>;
}
